// Schedule Inspection Panel
// Pull-out panel for scheduling future inspections

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ComplianceItemCard } from './compliance-item-card';
import { useNavigate } from 'react-router-dom';

// Form validation schema
const formSchema = z.object({
  scheduledDate: z.date({
    message: 'Scheduled date is required',
  }),
  assignedTo: z.string().min(1, 'Assigned inspector is required'),
  priority: z.enum(['Low', 'Medium', 'High'], {
    message: 'Priority is required',
  }),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ComplianceItem {
  id: number;
  assetId?: string;
  assetName: string;
  assetCode?: string;
  regimeCode?: string;
  regimeName: string;
  complianceCheckId?: number;
  dueDate?: string;
}

interface ScheduleInspectionPanelProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly complianceItem?: ComplianceItem;
  readonly onSubmit: (data: FormValues) => Promise<void>;
}

export function ScheduleInspectionPanel({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: Readonly<ScheduleInspectionPanelProps>) {
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      scheduledDate: undefined,
      assignedTo: '',
      priority: 'Medium',
      notes: '',
    },
  });

  const handleSubmit = async (data: FormValues) => {
    try {
      await onSubmit(data);
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error scheduling inspection:', error);
    }
  };

  const handleNavigateToAsset = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };

  const handleNavigateToRule = (ruleId: number) => {
    navigate(`/compliance/regulations?ruleId=${ruleId}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[540px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 bg-white dark:bg-black border-b">
          <SheetTitle className="text-lg font-semibold">Schedule Inspection</SheetTitle>
          <SheetDescription>
            Schedule a future inspection for this compliance requirement
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 bg-muted/30">
          <Form {...form}>
            <form id="schedule-inspection-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 pb-6 pt-6">
              {/* Compliance Item Info */}
              {complianceItem && (
                <ComplianceItemCard
                  assetId={complianceItem.assetId}
                  assetName={complianceItem.assetName}
                  assetCode={complianceItem.assetCode}
                  regimeCode={complianceItem.regimeCode || 'N/A'}
                  regimeName={complianceItem.regimeName}
                  complianceCheckId={complianceItem.complianceCheckId}
                  onNavigateToAsset={handleNavigateToAsset}
                  onNavigateToRule={handleNavigateToRule}
                />
              )}

              {/* Inspection Details */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Scheduled Date */}
                  <FormField
                    control={form.control}
                    name="scheduledDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="font-semibold">Scheduled Date *</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal bg-white dark:bg-gray-950',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription className="text-xs">
                          When should the inspection take place?
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Priority */}
                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold">Priority *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white dark:bg-gray-950">
                              <SelectValue placeholder="Select priority" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Low">Low</SelectItem>
                            <SelectItem value="Medium">Medium</SelectItem>
                            <SelectItem value="High">High</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription className="text-xs">
                          Inspection priority level
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Assigned To */}
                <FormField
                  control={form.control}
                  name="assignedTo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold">Assigned To *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter inspector name or ID" 
                          className="bg-white dark:bg-gray-950"
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Person responsible for conducting the inspection
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Notes */}
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold">Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Add any special instructions or notes for the inspection..."
                          className="min-h-[100px] bg-white dark:bg-gray-950"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Optional notes or special instructions for the inspector
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </form>
          </Form>
        </div>

        <SheetFooter className="border-t bg-white dark:bg-black px-6 py-4 mt-auto">
          <div className="flex gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="schedule-inspection-form"
              className="flex-1"
            >
              Schedule Inspection
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
